import React, { useState, useEffect } from 'react';

function TickerPage() {
  const [pair, setPair] = useState('');
  const [availablePairs, setAvailablePairs] = useState([]);
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pairsLoading, setPairsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch available pairs on component mount
  useEffect(() => {
    fetch('/api/config/pairs')
      .then(res => res.json())
      .then(data => {
        setAvailablePairs(data.pairs);
        setPair(data.default || data.pairs[0]);
        setPairsLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch pairs:', err);
        setPairsLoading(false);
      });
  }, []);

  useEffect(() => {
    // Don't fetch if pair is empty
    if (!pair) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/ticker?pair=${encodeURIComponent(pair)}`);
        const data = await response.json();

        if (data.error) {
          setError(data.error);
          console.error('Error:', data.error);
          return;
        }

        setStats(data.stats || {});
        setRows(data.rows || []);
      } catch (error) {
        setError('Failed to fetch ticker data: ' + error.message);
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [pair]);

  const handlePairChange = (e) => {
    const newPair = e.target.value;
    if (newPair) {
      setPair(newPair);
      setLoading(true);
    }
  };

  return (
    <div className="page-content">
      <div className="pair-selector">
        <label htmlFor="pair-select">Select Pair:</label>
        <select
          id="pair-select"
          value={pair}
          onChange={handlePairChange}
          className="pair-dropdown"
          disabled={pairsLoading}
        >
          {availablePairs.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      <div className="stats">
        {loading ? (
          <div className="no-data">Loading...</div>
        ) : Object.keys(stats).length === 0 ? (
          <div className="no-data">No data available</div>
        ) : (
          <>
            <div className="stat-card">
              <div className="label">Snapshots</div>
              <div className="value">{stats.count}</div>
            </div>
            <div className="stat-card">
              <div className="label">Current Price</div>
              <div className="value">${parseFloat(stats.current).toFixed(2)}</div>
            </div>
            <div className="stat-card">
              <div className="label">24h High</div>
              <div className="value">${parseFloat(stats.high_24h).toFixed(2)}</div>
            </div>
            <div className="stat-card">
              <div className="label">24h Low</div>
              <div className="value">${parseFloat(stats.low_24h).toFixed(2)}</div>
            </div>
            <div className="stat-card">
              <div className="label">Average</div>
              <div className="value">${parseFloat(stats.avg).toFixed(2)}</div>
            </div>
            <div className="stat-card">
              <div className="label">Spread</div>
              <div className="value">${parseFloat(stats.spread).toFixed(2)}</div>
            </div>
          </>
        )}
      </div>

      <h2 style={{ marginTop: '30px', marginBottom: '15px' }}>Recent Snapshots</h2>
      <table className="ticker-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Last Price</th>
            <th>Bid</th>
            <th>Ask</th>
            <th>Volume 24h</th>
            <th>VWAP 24h</th>
            <th>Fetched At</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr className="no-data">
              <td colSpan="7">No data yet. Fetching...</td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td className="price">${parseFloat(row.last_price).toFixed(2)}</td>
                <td className="price">${parseFloat(row.bid_price).toFixed(2)}</td>
                <td className="price">${parseFloat(row.ask_price).toFixed(2)}</td>
                <td>{parseFloat(row.volume_24h).toFixed(2)}</td>
                <td className="price">${parseFloat(row.vwap_24h).toFixed(2)}</td>
                <td className="time">
                  {row.fetched_at?.split('T')[1]?.split('.')[0] || row.fetched_at}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="refresh-info">
        🔄 This page auto-refreshes every 5 seconds
      </div>
      <div className="db-info">
        🐘 Using PostgreSQL database
      </div>
    </div>
  );
}

export default TickerPage;
